/**
 * ExpedienteExterno.tsx — M12: render moderno de los datos externos de MINSA
 * (vacunas, laboratorios y citas) para la pestaña Expediente.
 *
 * Diseño "fresco": tarjetas con icono de color por tipo, badge de fecha, chips
 * de estado y filas de detalle legibles. Las órdenes de laboratorio son
 * expandibles → resultados (analito/valor/rango) + botón de informe PDF.
 *
 * Extracción defensiva por nombres de campo probables (el esquema interno exacto
 * lo define MedisysApi); se afina cuando se observan respuestas reales con datos.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Calendar, Clock, FlaskConical, Loader2, MapPin,
  Stethoscope, Syringe, ChevronDown, AlertCircle, Download,
  Sparkles, TrendingUp, TrendingDown, AlertTriangle, Lightbulb,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../../config/firebase';
import {
  normalizeLabResult, ordenarResultados,
  type NormalizedLabResult, type LabEstado,
} from '../../utils/labResults';
import styles from './ExpedienteTab.module.css';

type PacienteCtx = { edad?: number | null; sexo?: string | null };

export interface ExternalState {
  loading: boolean;
  items: Record<string, unknown>[];
  /** true si la integración respondió (configurada); false → placeholder */
  disponible: boolean;
  /** Mensaje de error si la consulta falló (se muestra al médico). */
  error?: string;
  /** Cédula consultada en MINSA (para diagnóstico cuando no hay registros). */
  cedula?: string;
}

/* ── Field pickers (defensivos) ── */
function pickStr(obj: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = obj?.[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }
  return '';
}
function pickArr(obj: Record<string, unknown>, keys: string[]): unknown[] {
  for (const k of keys) {
    const v = obj?.[k];
    if (Array.isArray(v)) return v;
  }
  return [];
}

/* ── Parseo de una vacuna (shape MedisysApi: { vacuna:{...}, dosis:[], refuerzos:[] }) ── */
type VacEstado = 'completa' | 'pendiente' | 'sin_aplicar' | 'desconocido';
interface VacunaParsed {
  nombre: string; enfermedad: string; total: number;
  aplicadas: number; pendientes: number; ultimaFecha: string; ultimaMs: number; centro: string; estado: VacEstado;
}
function parseVacuna(v: Record<string, unknown>): VacunaParsed {
  const vac = (v.vacuna && typeof v.vacuna === 'object' ? v.vacuna : v) as Record<string, unknown>;
  const nombre = pickStr(vac, ['nombreGenerico', 'nombreVacuna', 'vacuna', 'nombre'])
    || pickStr(vac, ['enfermedades', 'enfermedad']) || 'Vacuna';
  const enfermedad = pickStr(vac, ['enfermedades', 'enfermedad']);
  const doses = [
    ...pickArr(v, ['dosis', 'Dosis']),
    ...pickArr(v, ['refuerzos', 'Refuerzos']),
  ] as Record<string, unknown>[];
  let aplicadas = 0, pendientes = 0, ultimaMs = -1, ultimaFecha = '', centro = '';
  for (const d of doses) {
    const est = pickStr(d, ['estadoVacunacion', 'estado', 'estadoDosis']).toUpperCase();
    const fr = pickStr(d, ['fechaVacunacion', 'fecha', 'fechaAplicacion']);
    if (/APLICAD/.test(est)) {
      aplicadas++;
      const dt = parseFecha(fr);
      if (dt && dt.getTime() > ultimaMs) {
        ultimaMs = dt.getTime();
        ultimaFecha = fmtFecha(fr);
        centro = pickStr(d, ['nombreCentro', 'centro']);
      }
    } else if (/PENDIENT/.test(est)) {
      pendientes++;
    }
  }
  const total = doses.length;
  const estado: VacEstado = total === 0 ? 'desconocido'
    : aplicadas > 0 && pendientes === 0 ? 'completa'
    : aplicadas > 0 ? 'pendiente'
    : 'sin_aplicar';
  return { nombre, enfermedad, total, aplicadas, pendientes, ultimaFecha, ultimaMs, centro, estado };
}
function vacEstadoMeta(e: VacEstado): { label: string; icon: string; pill: string; fill: string; card: string } {
  switch (e) {
    case 'completa': return { label: 'Aplicada', icon: styles.vacIconOk, pill: styles.vacPillOk, fill: styles.vacFillOk, card: styles.vacCardOk };
    case 'pendiente': return { label: 'Esquema parcial', icon: styles.vacIconPend, pill: styles.vacPillPend, fill: styles.vacFillPend, card: styles.vacCardPend };
    case 'sin_aplicar': return { label: 'Sin aplicar', icon: styles.vacIconSin, pill: styles.vacPillSin, fill: styles.vacFillSin, card: styles.vacCardSin };
    default: return { label: 'Registrada', icon: styles.vacIconSin, pill: styles.vacPillSin, fill: styles.vacFillSin, card: styles.vacCardSin };
  }
}

const DATE_KEYS = ['fechaHoraCita', 'FechaHoraCita', 'fecha', 'Fecha', 'fechaAplicacion', 'fechaCita', 'fechaOrden', 'fechaResultado', 'fechaHora', 'date', 'fechaProgramada'];
const ESTADO_KEYS = ['estado', 'Estado', 'status', 'Status'];

/* ── Fecha: parseo tolerante (ISO y dd/MM/yyyy [HH:mm]) ── */
function parseFecha(s: string): Date | null {
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const d = new Date(s);
    if (!isNaN(d.getTime())) return d;
  }
  const m = s.match(/(\d{1,2})[/-](\d{1,2})[/-](\d{4})(?:[ T](\d{1,2}):(\d{2}))?/);
  if (m) {
    const [, d, mo, y, h, mi] = m;
    return new Date(Number(y), Number(mo) - 1, Number(d), Number(h ?? '0'), Number(mi ?? '0'));
  }
  const fallback = new Date(s);
  return isNaN(fallback.getTime()) ? null : fallback;
}
function fmtFecha(s: string): string {
  if (!s) return '';
  const d = parseFecha(s);
  if (!d) return s;
  const base = d.toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' }).replace('.', '');
  const conHora = d.getHours() !== 0 || d.getMinutes() !== 0;
  return conHora ? `${base} · ${d.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })}` : base;
}

/* ── Chip de estado: color por palabra clave ── */
function estadoChip(estado: string): string {
  const e = estado.toLowerCase();
  if (/(complet|aplicad|vigente|normal|realizad|finaliz|disponible|atendid|confirmad)/.test(e)) return styles.extChipOk;
  if (/(pendient|vencid|atras|alto|bajo|anormal|incomplet|por aplicar|cancelad|ausent)/.test(e)) return styles.extChipWarn;
  if (/(program|futur|próxim|proxim|agendad)/.test(e)) return styles.extChipInfo;
  return styles.extChipNeutral;
}

/* ── Shell común: loading / sin datos / contenido ── */
function BodyShell({
  state, icon, emptyText, children,
}: {
  state: ExternalState;
  icon: ReactNode;
  emptyText: string;
  children: ReactNode;
}): ReactNode {
  if (state.loading) {
    return (
      <div className={styles.loadingState}>
        <Loader2 size={16} className={styles.spin} />
        <span>Consultando…</span>
      </div>
    );
  }
  // Error explícito de la consulta → mostrarlo (no ocultar el fallo en silencio).
  if (state.error) {
    return (
      <div className={styles.placeholderBody}>
        {icon}
        <p className={styles.placeholderText}>No se pudo consultar: {state.error}.</p>
      </div>
    );
  }
  // Sin datos: la integración respondió pero no hay registros.
  if (!state.disponible || state.items.length === 0) {
    return (
      <div className={styles.placeholderBody}>
        {icon}
        <p className={styles.placeholderText}>
          {emptyText}{state.cedula ? ` (cédula consultada: ${state.cedula})` : ''}
        </p>
      </div>
    );
  }
  return <div className={styles.sectionBody}>{children}</div>;
}

/* ── Análisis IA del expediente (labs / vacunas) ── */
interface AnalisisIA {
  resumen: string;
  hallazgos: Array<{ titulo: string; detalle: string; severidad: string }>;
  alertas: string[];
  sugerencias: string[];
}

const SEV_CLS: Record<string, string> = {
  critico: styles.sevCrit, alto: styles.sevHigh, bajo: styles.sevLow,
  leve: styles.sevMild, info: styles.sevInfo,
};

/** Llama a la IA (portal_analizarExpedienteIA) cuando hay ítems y está habilitado. */
function useAnalisisIA(
  tipo: 'labs' | 'vacunas',
  items: unknown[],
  paciente: PacienteCtx | undefined,
  enabled: boolean,
) {
  const [state, setState] = useState<{ loading: boolean; analisis: AnalisisIA | null }>({
    loading: false, analisis: null,
  });
  const key = enabled ? JSON.stringify(items).slice(0, 4000) : '';
  useEffect(() => {
    if (!enabled || !items || items.length === 0) return;
    let cancelled = false;
    setState({ loading: true, analisis: null });
    const fn = httpsCallable<
      { tipo: string; items: unknown[]; paciente?: PacienteCtx },
      AnalisisIA
    >(functions, 'portal_analizarExpedienteIA');
    fn({ tipo, items, paciente })
      .then((r) => { if (!cancelled) setState({ loading: false, analisis: r.data }); })
      .catch(() => { if (!cancelled) setState({ loading: false, analisis: null }); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return state;
}

/** Tira de conteos por severidad (infografía determinista desde los resultados). */
function SevStrip({ results }: { results: NormalizedLabResult[] }): ReactNode {
  const n: Record<string, number> = { critico: 0, alto: 0, bajo: 0, normal: 0 };
  for (const r of results) if (r.estado in n) n[r.estado]++;
  const pills: Array<{ k: string; label: string; cls: string; Icon: typeof TrendingUp | null }> = [
    { k: 'critico', label: 'crítico', cls: styles.pillCrit, Icon: AlertTriangle },
    { k: 'alto', label: 'alto', cls: styles.pillHigh, Icon: TrendingUp },
    { k: 'bajo', label: 'bajo', cls: styles.pillLow, Icon: TrendingDown },
    { k: 'normal', label: 'normal', cls: styles.pillNorm, Icon: null },
  ];
  return (
    <div className={styles.sevStrip}>
      {pills.filter((p) => n[p.k] > 0).map((p) => {
        const Icon = p.Icon;
        return (
          <span key={p.k} className={`${styles.sevPill} ${p.cls}`}>
            {Icon && <Icon size={12} />}<strong>{n[p.k]}</strong> {p.label}
          </span>
        );
      })}
    </div>
  );
}

/** Panel IA compacto, estilo infografía (resumen 1 línea + chips + mini-líneas). */
function AnalisisIAPanel({ loading, analisis }: { loading: boolean; analisis: AnalisisIA | null }): ReactNode {
  if (loading) {
    return (
      <div className={styles.aiPanel}>
        <span className={styles.aiTag}><Sparkles size={11} /> IA</span>
        <span className={styles.aiInline}><Loader2 size={12} className={styles.spin} /> Analizando…</span>
      </div>
    );
  }
  if (!analisis) return null;
  const { resumen, hallazgos, alertas, sugerencias } = analisis;
  if (!resumen && !hallazgos.length && !alertas.length && !sugerencias.length) return null;
  return (
    <div className={styles.aiPanel}>
      {resumen && (
        <p className={styles.aiResumen}>
          <span className={styles.aiTag}><Sparkles size={11} /> IA</span> {resumen}
        </p>
      )}
      {hallazgos.length > 0 && (
        <div className={styles.aiChips}>
          {hallazgos.slice(0, 5).map((h, i) => (
            <span
              key={i}
              className={`${styles.aiChip} ${SEV_CLS[h.severidad?.toLowerCase()] ?? styles.sevInfo}`}
              title={h.detalle || h.titulo}
            >
              {h.titulo}{h.detalle ? <span className={styles.aiChipDet}> · {h.detalle}</span> : null}
            </span>
          ))}
        </div>
      )}
      {(alertas.length > 0 || sugerencias.length > 0) && (
        <div className={styles.aiFoot}>
          {alertas.slice(0, 3).map((a, i) => (
            <span key={`a${i}`} className={`${styles.aiMini} ${styles.aiMiniAlert}`}><AlertTriangle size={10} /> {a}</span>
          ))}
          {sugerencias.slice(0, 3).map((s, i) => (
            <span key={`s${i}`} className={`${styles.aiMini} ${styles.aiMiniSug}`}><Lightbulb size={10} /> {s}</span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Fila de resultado de laboratorio (moderna, color por estado) ── */
function estadoMeta(e: LabEstado): { cls: string; chip: string; label: string; Icon: typeof TrendingUp | null } {
  switch (e) {
    case 'critico': return { cls: styles.rowCrit, chip: styles.chipCrit, label: 'Crítico', Icon: AlertTriangle };
    case 'alto': return { cls: styles.rowHigh, chip: styles.chipHigh, label: 'Alto', Icon: TrendingUp };
    case 'bajo': return { cls: styles.rowLow, chip: styles.chipLow, label: 'Bajo', Icon: TrendingDown };
    case 'normal': return { cls: '', chip: styles.chipNorm, label: 'Normal', Icon: null };
    default: return { cls: '', chip: '', label: '', Icon: null };
  }
}

function LabResultRow({ r }: { r: NormalizedLabResult }): ReactNode {
  const m = estadoMeta(r.estado);
  const Icon = m.Icon;
  return (
    <div className={`${styles.labRow} ${m.cls}`}>
      <div className={styles.labRowMain}>
        <span className={styles.labRowName}>{r.nombre}</span>
        <span className={styles.labRowVal}>
          {Icon && <Icon size={12} className={styles.labRowTrend} />}
          <strong>{r.valor || '—'}</strong>{r.unidad ? <span className={styles.labRowUnit}> {r.unidad}</span> : null}
        </span>
        {m.label && <span className={`${styles.labChip} ${m.chip}`}>{m.label}</span>}
      </div>
      {r.rango && <div className={styles.labRowRange}>Referencia: {r.rango}</div>}
    </div>
  );
}

/* ── 1. Vacunas ── */
export function VacunasBody({ state, paciente }: { state: ExternalState; paciente?: PacienteCtx }): ReactNode {
  // Para la IA: forma simplificada (la API anida bajo v.vacuna + dosis/refuerzos).
  const iaItems = useMemo(
    () => state.items.map((v) => {
      const p = parseVacuna(v);
      return {
        vacuna: p.nombre, enfermedad: p.enfermedad,
        aplicadas: p.aplicadas, pendientes: p.pendientes,
        ultimaFecha: p.ultimaFecha, estado: p.estado,
      };
    }),
    [state.items],
  );
  const ia = useAnalisisIA('vacunas', iaItems, paciente, !state.loading && iaItems.length > 0);
  // Tarjetas SOLO para vacunas con ≥1 dosis aplicada (las pendientes ya se resumen
  // arriba en el panel IA). Deduplica idénticas y ordena por aplicación más reciente.
  const cards = useMemo(() => {
    const seen = new Set<string>();
    const list = state.items
      .map((v) => parseVacuna(v as Record<string, unknown>))
      .filter((p) => p.aplicadas > 0)
      .filter((p) => {
        const key = `${p.nombre.toLowerCase().trim()}|${p.aplicadas}|${p.pendientes}|${p.ultimaFecha}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    list.sort((a, b) => b.ultimaMs - a.ultimaMs);
    return list;
  }, [state.items]);
  return (
    <BodyShell
      state={state}
      icon={<Syringe size={24} className={styles.placeholderIcon} />}
      emptyText="No hay vacunas registradas."
    >
      <AnalisisIAPanel loading={ia.loading} analisis={ia.analisis} />
      {cards.length === 0 ? (
        <p className={styles.emptyItemsSm}><Syringe size={12} /> Sin dosis aplicadas registradas. Revise el esquema pendiente en el resumen.</p>
      ) : (
        <div className={styles.vacGrid}>
          {cards.slice(0, 60).map((p, i) => {
            const total = Math.max(p.total, p.aplicadas + p.pendientes);
            const pct = total > 0 ? Math.round((p.aplicadas / total) * 100) : 100;
            const m = vacEstadoMeta(p.estado);
            return (
              <article key={i} className={`${styles.vacCard} ${m.card}`}>
                <div className={styles.vacCardTop}>
                  <span className={`${styles.vacIcon} ${m.icon}`}><Syringe size={18} /></span>
                  <span className={`${styles.vacStatusPill} ${m.pill}`}><span className={styles.vacStatusDot} /> {m.label}</span>
                </div>
                <div className={styles.vacCardBody}>
                  <h4 className={styles.vacName}>{p.nombre}</h4>
                  {p.enfermedad && p.enfermedad !== p.nombre && <p className={styles.vacDisease}>{p.enfermedad}</p>}
                </div>
                {total > 0 && (
                  <div className={styles.vacProgress}>
                    <div className={styles.vacProgressHead}>
                      <span className={styles.vacProgressLabel}>{p.aplicadas} de {total} dosis</span>
                      {p.pendientes > 0 && <span className={styles.vacProgressPend}>{p.pendientes} pendiente{p.pendientes > 1 ? 's' : ''}</span>}
                    </div>
                    <div className={styles.vacTrack}><div className={`${styles.vacFill} ${m.fill}`} style={{ width: `${pct}%` }} /></div>
                  </div>
                )}
                {(p.ultimaFecha || p.centro) && (
                  <div className={styles.vacMeta}>
                    {p.ultimaFecha && <span><Calendar size={12} /> {p.ultimaFecha}</span>}
                    {p.centro && <span><MapPin size={12} /> {p.centro}</span>}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </BodyShell>
  );
}

/* ── 2. Citas ── */
export function CitasBody({ state }: { state: ExternalState }): ReactNode {
  const hoy = new Date();
  return (
    <BodyShell
      state={state}
      icon={<Calendar size={24} className={styles.placeholderIcon} />}
      emptyText="No hay citas programadas registradas."
    >
      <div className={styles.extList}>
        {state.items.slice(0, 50).map((c, i) => {
          const fechaRaw = pickStr(c, DATE_KEYS);
          const d = parseFecha(fechaRaw);
          const titulo = pickStr(c, ['especialidad', 'Especialidad', 'servicio', 'nombreServicio', 'motivo', 'descripcion', 'tipo']) || 'Cita médica';
          const medico = pickStr(c, ['medico', 'Medico', 'doctor', 'profesional']);
          const lugar = pickStr(c, ['lugar', 'institucion', 'Institucion', 'sede', 'centro', 'establecimiento']);
          const estado = pickStr(c, ESTADO_KEYS);
          const futura = d ? d.getTime() >= hoy.getTime() : false;
          return (
            <div key={i} className={`${styles.extCard} ${futura ? styles.extCardCitaFutura : ''}`}>
              <div className={styles.extDateBadge}>
                {d ? (
                  <>
                    <span className={styles.extDateDay}>{String(d.getDate()).padStart(2, '0')}</span>
                    <span className={styles.extDateMonth}>{d.toLocaleDateString('es', { month: 'short' }).replace('.', '')}</span>
                  </>
                ) : (
                  <Calendar size={18} />
                )}
              </div>
              <div className={styles.extMain}>
                <div className={styles.extTitleRow}>
                  <span className={styles.extTitle}>{titulo}</span>
                  {futura
                    ? <span className={`${styles.extChip} ${styles.extChipInfo}`}>Próxima</span>
                    : <span className={`${styles.extChip} ${styles.extChipNeutral}`}>Anterior</span>}
                  {estado && <span className={`${styles.extChip} ${estadoChip(estado)}`}>{estado}</span>}
                </div>
                <div className={styles.extMeta}>
                  {d && (d.getHours() !== 0 || d.getMinutes() !== 0) && (
                    <span><Clock size={11} /> {d.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })}</span>
                  )}
                  {medico && <span><Stethoscope size={11} /> {medico}</span>}
                  {lugar && <span><MapPin size={11} /> {lugar}</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </BodyShell>
  );
}

/* ── 3. Laboratorios (órdenes expandibles → resultados + PDF) ── */
export function LabsBody({ state, paciente, consultaId }: { state: ExternalState; paciente?: PacienteCtx; consultaId?: string }): ReactNode {
  return (
    <BodyShell
      state={state}
      icon={<FlaskConical size={24} className={styles.placeholderIcon} />}
      emptyText="No hay resultados de exámenes registrados."
    >
      <div className={styles.extList}>
        {state.items.slice(0, 50).map((o, i) => (
          <LabOrderCard key={i} orden={o} paciente={paciente} consultaId={consultaId} />
        ))}
      </div>
    </BodyShell>
  );
}

function abrirPdf(pdf64: string): void {
  try {
    const limpio = pdf64.replace(/^data:application\/pdf;base64,/, '');
    const bytes = Uint8Array.from(atob(limpio), (c) => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    console.error('[ExpedienteExterno] no se pudo abrir el PDF:', e);
  }
}

function LabOrderCard({ orden, paciente, consultaId }: { orden: Record<string, unknown>; paciente?: PacienteCtx; consultaId?: string }): ReactNode {
  const numeroOrden = pickStr(orden, ['numeroOrden', 'NumeroOrden', 'numero', 'Numero', 'orden', 'id']);
  const codigoSede = pickStr(orden, ['codigoSede', 'CodigoSede', 'sede', 'Sede', 'codSede']);
  const titulo = pickStr(orden, ['descripcion', 'examen', 'nombre', 'estudio', 'prueba']) || (numeroOrden ? `Orden ${numeroOrden}` : 'Orden de laboratorio');
  const fecha = fmtFecha(pickStr(orden, DATE_KEYS));
  const estado = pickStr(orden, ESTADO_KEYS);
  const puedeDetalle = !!(numeroOrden && codigoSede);

  const [open, setOpen] = useState(false);
  const [res, setRes] = useState<{ loading: boolean; items: Record<string, unknown>[]; cargado: boolean }>({ loading: false, items: [], cargado: false });
  const [pdfLoading, setPdfLoading] = useState(false);

  // Normaliza + ordena (alterados primero) y dispara el análisis IA al abrir.
  const normalizados = useMemo(
    () => (res.items.length ? ordenarResultados(res.items.map(normalizeLabResult)) : []),
    [res.items],
  );
  const ia = useAnalisisIA('labs', normalizados, paciente, open && res.cargado && normalizados.length > 0);

  const toggle = useCallback(async () => {
    const next = !open;
    setOpen(next);
    if (!next || res.cargado || res.loading || !puedeDetalle) return;
    setRes((s) => ({ ...s, loading: true }));
    try {
      const fn = httpsCallable<{ numeroOrden: string; codigoSede: string; consultaId?: string; idToken?: string }, { resultados?: Record<string, unknown>[] }>(functions, 'portal_obtenerResultadosLab');
      const idToken = await auth.currentUser?.getIdToken().catch(() => undefined);
      const r = await fn({ numeroOrden, codigoSede, consultaId, idToken });
      setRes({ loading: false, items: r.data?.resultados ?? [], cargado: true });
    } catch {
      setRes({ loading: false, items: [], cargado: true });
    }
  }, [open, res.cargado, res.loading, puedeDetalle, numeroOrden, codigoSede, consultaId]);

  const verPdf = useCallback(async () => {
    if (!puedeDetalle || pdfLoading) return;
    setPdfLoading(true);
    try {
      const fn = httpsCallable<{ numeroOrden: string; codigoSede: string; consultaId?: string; idToken?: string }, { pdf64?: string | null }>(functions, 'portal_obtenerInformeLab');
      const idToken = await auth.currentUser?.getIdToken().catch(() => undefined);
      const r = await fn({ numeroOrden, codigoSede, consultaId, idToken });
      if (r.data?.pdf64) abrirPdf(r.data.pdf64);
    } catch (e) {
      console.error('[ExpedienteExterno] informe error:', e);
    } finally {
      setPdfLoading(false);
    }
  }, [puedeDetalle, pdfLoading, numeroOrden, codigoSede, consultaId]);

  return (
    <div className={`${styles.extCard} ${styles.extCardLab} ${open ? styles.extCardOpen : ''}`}>
      <div className={`${styles.extIcon} ${styles.extIconLab}`}><FlaskConical size={15} /></div>
      <div className={styles.extMain}>
        <div className={styles.extTitleRow}>
          <span className={styles.extTitle}>{titulo}</span>
          {estado && <span className={`${styles.extChip} ${estadoChip(estado)}`}>{estado}</span>}
        </div>
        <div className={styles.extMeta}>
          {fecha && <span><Calendar size={11} /> {fecha}</span>}
          {numeroOrden && <span>Orden {numeroOrden}</span>}
          {codigoSede && <span>Sede {codigoSede}</span>}
        </div>

        {puedeDetalle && (
          <div className={styles.extActions}>
            <button type="button" className={styles.extActionBtn} onClick={toggle} aria-expanded={open}>
              <ChevronDown size={13} className={`${styles.extChevron} ${open ? styles.extChevronOpen : ''}`} />
              {open ? 'Ocultar resultados' : 'Ver resultados'}
            </button>
            <button type="button" className={styles.extActionBtn} onClick={verPdf} disabled={pdfLoading}>
              {pdfLoading ? <Loader2 size={13} className={styles.spin} /> : <Download size={13} />}
              Informe PDF
            </button>
          </div>
        )}

        {open && (
          <div className={styles.labResWrap}>
            {res.loading ? (
              <div className={styles.loadingStateSm}><Loader2 size={13} className={styles.spin} /> <span>Cargando resultados…</span></div>
            ) : normalizados.length === 0 ? (
              <p className={styles.emptyItemsSm}><AlertCircle size={12} /> Sin resultados disponibles para esta orden.</p>
            ) : (
              <>
                <SevStrip results={normalizados} />
                <AnalisisIAPanel loading={ia.loading} analisis={ia.analisis} />
                <div className={styles.labResList}>
                  {normalizados.slice(0, 80).map((x, i) => <LabResultRow key={i} r={x} />)}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
